import { observable, action, reaction, toJS } from 'mobx';
import storageUtil from './storageUtil';

const STORAGE_KEY = 'onboardingData';

class OnboardingStore {
    // Identity verification data (Step 1)
    @observable identityData = {
        idNumber: '',
        faceVerification: null, // { match: boolean, confidenceScore: number, timestamp: date }
        verified: false,
        completed: false
    };

    // Business selection data (Step 2)
    @observable businessData = {
        businesses: [], // All businesses fetched from CIPC
        selectedBusiness: null, // { registrationNumber, name, status, type, registrationDate }
        completed: false
    };

    // Bank account linking data (Step 3)
    @observable bankData = {
        linkMethod: null, // 'link' | 'upload' | 'skip'
        linkedBank: null, // 'standard' | 'fnb' | 'nedbank'
        accountData: null, // { accountHolderName, accounts: [...] }
        transactionData: null, // { transactions: [...] }
        uploadedFiles: null, // For statement uploads
        completed: false
    };

    // Past invoices data (Step 4)
    @observable pastInvoices = {
        uploadMethod: null, // 'upload' | 'skip'
        files: [],
        completed: false
    };

    // Accounting system data (Step 5)
    @observable accountingSystem = {
        linkMethod: null, // 'select_system' | 'upload_accounts'
        selectedSystem: null, // Xero ID or null
        fileName: null, // For management accounts upload
        completed: false
    };

    // Onboarding progress tracking
    @observable progress = {
        currentStep: 1,
        stepsCompleted: {
            step1: false,
            step2: false,
            step3: false,
            step4: false,
            step5: false
        }
    };

    constructor() {
        // Load saved data from localStorage
        this.loadFromStorage();

        // Auto-sync to localStorage on any change
        reaction(
            () => JSON.stringify({
                identityData: this.identityData,
                businessData: this.businessData,
                bankData: this.bankData,
                pastInvoices: this.pastInvoices,
                accountingSystem: this.accountingSystem,
                progress: this.progress
            }),
            (data) => {
                this.syncToStorage();
            }
        );
    }

    // Load data from localStorage
    @action loadFromStorage() {
        const savedData = storageUtil.getItem(STORAGE_KEY);
        if (savedData) {
            if (savedData.identityData) {
                this.identityData = { ...this.identityData, ...savedData.identityData };
            }
            if (savedData.businessData) {
                this.businessData = { ...this.businessData, ...savedData.businessData };
            }
            if (savedData.bankData) {
                this.bankData = { ...this.bankData, ...savedData.bankData };
            }
            if (savedData.pastInvoices) {
                this.pastInvoices = { ...this.pastInvoices, ...savedData.pastInvoices };
            }
            if (savedData.accountingSystem) {
                this.accountingSystem = { ...this.accountingSystem, ...savedData.accountingSystem };
            }
            if (savedData.progress) {
                this.progress = { ...this.progress, ...savedData.progress };
            }
        }
    }

    // Sync current state to localStorage
    @action syncToStorage() {
        const dataToStore = {
            identityData: this.identityData,
            businessData: this.businessData,
            bankData: this.bankData,
            pastInvoices: this.pastInvoices,
            accountingSystem: this.accountingSystem,
            progress: this.progress
        };
        storageUtil.setItem(STORAGE_KEY, dataToStore);
    }

    // ========== Step 1: Identity Verification Actions ==========

    @action setIdNumber(idNumber) {
        this.identityData.idNumber = idNumber;
    }

    @action setFaceVerification(verificationResult) {
        this.identityData.faceVerification = {
            ...verificationResult,
            timestamp: new Date().toISOString()
        };
        this.identityData.verified = true;
    }

    @action completeStep1() {
        this.identityData.completed = true;
        this.progress.stepsCompleted.step1 = true;
        this.progress.currentStep = 2;
    }

    // ========== Step 2: Business Selection Actions ==========

    @action setBusinesses(businesses) {
        this.businessData.businesses = businesses;
    }

    @action selectBusiness(business) {
        this.businessData.selectedBusiness = {
            registrationNumber: business.registrationNumber,
            name: business.name,
            status: business.status,
            type: business.type,
            registrationDate: business.registrationDate
        };
    }

    @action completeStep2() {
        this.businessData.completed = true;
        this.progress.stepsCompleted.step2 = true;
        this.progress.currentStep = 3;
    }

    // ========== Step 3: Bank Account Linking Actions ==========

    @action setBankLinkMethod(method) {
        this.bankData.linkMethod = method;
    }

    @action setBankData(bank, accountData, transactionData) {
        this.bankData.linkedBank = bank;
        this.bankData.accountData = accountData;
        this.bankData.transactionData = transactionData;
        this.bankData.linkMethod = 'link';
    }

    @action setBankUpload(files) {
        this.bankData.uploadedFiles = files;
        this.bankData.linkMethod = 'upload';
    }

    @action skipBankLink() {
        this.bankData.linkMethod = 'skip';
    }

    @action completeStep3() {
        this.bankData.completed = true;
        this.progress.stepsCompleted.step3 = true;
        this.progress.currentStep = 4;
    }

    // ========== Step 4: Past Invoices Actions ==========

    @action setPastInvoiceMethod(method) {
        this.pastInvoices.uploadMethod = method;
    }

    @action setPastInvoiceFiles(files) {
        this.pastInvoices.files = files;
    }

    @action skipPastInvoices() {
        this.pastInvoices.uploadMethod = 'skip';
        this.pastInvoices.files = [];
        this.pastInvoices.completed = true;
    }

    @action completeStep4() {
        this.pastInvoices.completed = true;
        this.progress.stepsCompleted.step4 = true;
        this.progress.currentStep = 5;
    }

    // ========== Step 5: Accounting System Actions ==========

    @action setAccountingSystem(linkMethod, selectedSystem, fileName) {
        this.accountingSystem.linkMethod = linkMethod;
        this.accountingSystem.selectedSystem = selectedSystem;
        this.accountingSystem.fileName = fileName;
    }

    @action completeStep5() {
        this.accountingSystem.completed = true;
        this.progress.stepsCompleted.step5 = true;
    }

    // ========== General Progress Actions ==========

    @action setCurrentStep(stepNumber) {
        this.progress.currentStep = stepNumber;
    }

    @action markStepComplete(stepNumber) {
        const stepKey = `step${stepNumber}`;
        if (this.progress.stepsCompleted.hasOwnProperty(stepKey)) {
            this.progress.stepsCompleted[stepKey] = true;
        }
    }

    // ========== Reset/Clear Actions ==========

    @action resetOnboarding() {
        this.identityData = {
            idNumber: '',
            faceVerification: null,
            verified: false,
            completed: false
        };
        this.businessData = {
            businesses: [],
            selectedBusiness: null,
            completed: false
        };
        this.bankData = {
            linkMethod: null,
            linkedBank: null,
            accountData: null,
            transactionData: null,
            uploadedFiles: null,
            completed: false
        };
        this.pastInvoices = {
            uploadMethod: null,
            files: [],
            completed: false
        };
        this.accountingSystem = {
            linkMethod: null,
            selectedSystem: null,
            fileName: null,
            completed: false
        };
        this.progress = {
            currentStep: 1,
            stepsCompleted: {
                step1: false,
                step2: false,
                step3: false,
                step4: false,
                step5: false
            }
        };
        storageUtil.removeItem(STORAGE_KEY);
    }

    @action clearOnCompletion() {
        // Clear onboarding data when the entire onboarding is complete
        this.resetOnboarding();
    }

    // ========== Data Collection for Backend Submission ==========

    @action getOnboardingData() {
        return {
            identityData: toJS(this.identityData),
            businessData: toJS(this.businessData),
            bankData: toJS(this.bankData),
            pastInvoices: toJS(this.pastInvoices),
            accountingSystem: toJS(this.accountingSystem)
        };
    }

    // ========== Getters ==========

    get hasIdentityData() {
        return !!(this.identityData.idNumber && this.identityData.verified);
    }

    get hasBusinessData() {
        return !!(this.businessData.selectedBusiness);
    }

    get hasBankData() {
        return !!(this.bankData.linkMethod);
    }

    get hasPastInvoiceData() {
        return !!(this.pastInvoices.uploadMethod);
    }

    get hasAccountingSystemData() {
        return !!(this.accountingSystem.linkMethod);
    }

    get canResumeOnboarding() {
        return this.progress.currentStep > 1 || this.progress.stepsCompleted.step1;
    }
}

const onboardingStore = new OnboardingStore();
export default onboardingStore;
